import type { PersistedCriteria, SaveableCriteria } from '~/composables/useSearchCriteria'
import { SEARCH_FILTERS } from '~~/shared/search-constants'
import { parseSejoursParam, type SejourSlug } from '~~/shared/establishment-filters'
import { REGIONS } from '~~/shared/regions'
import { homeTypeFromSearchType, pseoTypeFromHomeType } from '~/utils/pseo-guest-query'
import type { HomeSearchLocation, HomeSearchType } from '~/utils/home-search-route'
import { toIsoDate, upcomingWeekend, type WizardDateRange } from '~/utils/wizard-dates'

/**
 * Mémoire de session du parcours de l'accueil (`HoliHomeSearchWizard`) :
 * conversions pures entre les critères mémorisés (`useSearchCriteria`,
 * sessionStorage) et les réponses du parcours. Le composant garde les effets
 * (cocher le type et le poser dans l'URL, changer d'étape, écrire la mémoire).
 */

/** Lieu retenu à l'étape 2 : suggestion, chip de région ou « Partout en France ». */
export interface WizardPlace extends HomeSearchLocation {
  /** Chambres d'hôtes et gîtes du lieu (nombre de maisons selon le type). */
  cdh?: number
  gite?: number
}

/** Option de l'étape 3 : « Ce week-end », « Choisir mes dates », « Je ne sais pas encore ». */
export type WhenOption = 'weekend' | 'pick' | 'unknown'

export type DateRange = { start: string; end: string }

/** Réponses du parcours écrites en mémoire. */
export interface WizardAnswers {
  type: HomeSearchType | null
  place: WizardPlace | null
  when: WhenOption | null
  picked: WizardDateRange
  adults: number
  children: number
  sejours: readonly SejourSlug[]
}

/** Réponses relues de la mémoire ; `null` = rien à poser. */
export interface RememberedAnswers {
  type: HomeSearchType | null
  place: WizardPlace | null
  when: WhenOption | null
  /** Plage de « Choisir mes dates » (seulement avec `when: 'pick'`). */
  picked: DateRange | null
  /** Valeurs brutes, bornées par `useGuestCounters` à la pose. */
  adults: number
  children: number
  /**
   * Séjours types connus (`parseSejoursParam` : triés, dédoublonnés, slugs
   * inconnus ignorés) ; `null` si la mémoire n'en porte pas.
   */
  sejours: SejourSlug[] | null
}

/** Région connue du parcours : ses comptes de maisons pour la ligne du lieu. */
export interface MemoryRegionCounts {
  slug: string
  cdhCount: number
  giteCount: number
}

const ISO_DATE = /^\d{4}-\d{2}-\d{2}$/

/** Dates de l'option active : le week-end donné, la plage complète choisie, ou aucune. */
export function datesOfAnswer(when: WhenOption | null, picked: WizardDateRange, weekend: DateRange): DateRange | null {
  if (when === 'weekend') return weekend
  if (when === 'pick' && picked.start && picked.end) return { start: picked.start, end: picked.end }
  return null
}

/** Lieu mémorisé : ville, région (par nom) ou département (par code) connus de `REGIONS`, ou « Partout en France ». */
function placeFromMemory(
  memory: PersistedCriteria,
  regions: readonly MemoryRegionCounts[],
  everywhereLabel: string,
): WizardPlace | null {
  if (typeof memory.city === 'string' && memory.city) return { type: 'city', value: memory.city }
  if (typeof memory.region === 'string' && memory.region) {
    const region = REGIONS.find(r => r.name === memory.region)
    if (!region) return null
    const counts = regions.find(r => r.slug === region.slug)
    return { type: 'region', value: region.name, slug: region.slug, cdh: counts?.cdhCount, gite: counts?.giteCount }
  }
  if (typeof memory.department === 'string' && memory.department) {
    const code = memory.department.toUpperCase()
    const department = REGIONS.flatMap(r => r.departments).find(d => d.code === code)
    if (!department) return null
    const name = typeof memory.departmentName === 'string' && memory.departmentName ? memory.departmentName : department.name
    return { type: 'department', value: name, code: department.code, slug: department.slug }
  }
  if (memory.everywhere === true) return { type: 'france', value: everywhereLabel }
  return null
}

/** Dates mémorisées : passées → retirées ; week-end qui vient → « Ce week-end » ; sinon « Choisir mes dates ». */
function datesFromMemory(memory: PersistedCriteria, today: Date): Pick<RememberedAnswers, 'when' | 'picked'> {
  const { checkIn, checkOut } = memory
  const hasDates = typeof checkIn === 'string' && ISO_DATE.test(checkIn)
    && typeof checkOut === 'string' && ISO_DATE.test(checkOut)
  if (hasDates) {
    if (checkIn < toIsoDate(today) || checkOut <= checkIn) return { when: null, picked: null }
    const coming = upcomingWeekend(today)
    if (checkIn === coming.start && checkOut === coming.end) return { when: 'weekend', picked: null }
    return { when: 'pick', picked: { start: checkIn, end: checkOut } }
  }
  if (!checkIn && !checkOut && memory.datesFree === true) return { when: 'unknown', picked: null }
  return { when: null, picked: null }
}

/**
 * Mémoire → réponses du parcours.
 *
 * Type absent = Tout : « Les deux » seulement si la mémoire porte un lieu (une
 * recherche localisée a toujours un type) ; sinon (mémoire partielle de
 * l'overlay : dates, voyageurs) aucun type. Les séjours types sont rendus tels
 * quels : `table-dhotes` est retiré par l'appelant selon le type retenu
 * (`?type` de l'URL l'emporte sur la mémoire). Les envies booléennes
 * (`amenities`) ne sont pas relues : le parcours ne les montre plus.
 */
export function answersFromMemory(
  memory: PersistedCriteria,
  options: { today: Date; regions: readonly MemoryRegionCounts[]; everywhereLabel: string },
): RememberedAnswers {
  const place = placeFromMemory(memory, options.regions, options.everywhereLabel)
  const type = homeTypeFromSearchType(memory.type) ?? (place ? 'all' : null)
  const sejours = Array.isArray(memory.sejours) ? parseSejoursParam(memory.sejours) : null
  return {
    type,
    place,
    ...datesFromMemory(memory, options.today),
    adults: memory.minAdults ?? SEARCH_FILTERS.DEFAULT_ADULTS,
    children: memory.minChildren ?? SEARCH_FILTERS.DEFAULT_CHILDREN,
    sejours,
  }
}

/**
 * Réponses du parcours → critères à mémoriser (dates de l'option active ;
 * jamais d'âges d'enfants ; séjours types, jamais d'envies booléennes).
 */
export function memoryFromAnswers(answers: WizardAnswers, weekend: DateRange): SaveableCriteria {
  const filters: SaveableCriteria = {
    minAdults: answers.adults,
    minChildren: answers.children,
  }
  if (answers.type && answers.type !== 'all') filters.type = pseoTypeFromHomeType(answers.type)

  const p = answers.place
  if (p?.type === 'city') filters.city = p.value
  else if (p?.type === 'region') filters.region = p.value
  else if (p?.type === 'department' && p.code) {
    filters.department = p.code
    filters.departmentName = p.value
  }
  else if (p?.type === 'france') filters.everywhere = true

  const dates = datesOfAnswer(answers.when, answers.picked, weekend)
  if (dates) {
    filters.checkIn = dates.start
    filters.checkOut = dates.end
  }
  if (answers.when === 'unknown') filters.datesFree = true

  const sejours = parseSejoursParam(answers.sejours)
  if (sejours.length > 0) filters.sejours = sejours
  return filters
}
